'use client';

import { useState } from 'react';
import { styled } from 'styled-components';
import cs from 'classnames';
import Link from 'next/link';
import { Icon } from '@wistia/ui';
import { TRANSITION_DURATION } from '@content/navigation/navData';
import { PillarCategory } from '@content/navigation/menuStyles';
import TextLink from '@dvd/components/TextLink';
import { trackLink } from '@lib/tracking';

const MenuLink = styled(Link)<{ $accentColor: string }>`
  && {
    align-items: center;
    color: ${({ theme }) => theme.colors.grey800};
    display: inline-flex;

    /** custom per design */
    font-family: var(--font-walsheim);
    font-size: ${({ theme }) => theme.typography.fontSize.headingFour};
    font-weight: ${({ theme }) => theme.typography.fontWeight.link};
    line-height: ${({ theme }) => theme.typography.lineHeight.headingFour};
    padding-block: ${({ theme }) => theme.spacing[0.75]};
    transition: color ${TRANSITION_DURATION} linear;
    width: 100%;

    &:hover,
    &:active {
      color: ${(props) => props.$accentColor};
    }
  }
`;

const MenuIcon = styled(Icon)`
  color: currentcolor;

  /** customer per design */
  margin-inline-start: 0.375rem;
  transition: transform ${TRANSITION_DURATION} linear;

  .isActive & {
    /** matches Legacy */
    transform: rotateX(180deg) translateY(1px);
  }
`;

const MenuContainer = styled.div`
  height: 0;
  overflow: hidden;
  pointer-events: none;
  visibility: hidden;

  &.isOpen {
    height: min-content;
    overflow: visible;
    pointer-events: auto;
    visibility: visible;
  }
`;

const MenuNav = styled.div`
  display: flex;
  flex-direction: column;
  padding-block: ${({ theme }) => theme.spacing[0.75]}
    ${({ theme }) => theme.spacing[1.5]};
  padding-inline: 2px;
  row-gap: ${({ theme }) => theme.spacing[0.75]};
`;

const MenuCta = styled(TextLink)<{
  $accentColor: string;
}>`
  /** custom per design: white pill on the blue100 drawer */
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radius.btn};
  color: ${(props) => props.$accentColor};
  font-family: var(--font-walsheim);
  font-size: ${({ theme }) => theme.typography.fontSize.headingFour};
  font-weight: ${({ theme }) => theme.typography.fontWeight.link};
  /** custom per design: Space05 (20px) space above/below the button — 8px margin
   * stacks with the 12px drawer padding-top and 12px row-gap; not internal padding */
  margin-block: ${({ theme }) => theme.spacing[0.5]};
  padding-block: ${({ theme }) => theme.spacing[0.75]};
  text-align: center;

  &:hover,
  &:active {
    color: ${(props) => props.$accentColor};
  }

  &:visited {
    color: ${(props) => props.$accentColor};
  }
`;

const PillarGroup = styled.div``;

const PillarLink = styled(Link)<{
  $accentColor: string;
}>`
  /** custom per design */
  align-items: flex-start;
  border-radius: ${({ theme }) => theme.radius.mini};
  color: ${({ theme }) => theme.colors.blue700};
  display: flex;
  flex-direction: column;
  font-family: var(--font-walsheim);
  font-size: ${({ theme }) => theme.typography.fontSize.headingFour};
  font-weight: ${({ theme }) => theme.typography.fontWeight.link};

  /** custom per design */
  gap: ${({ theme }) => theme.spacing[0.25]};
  line-height: ${({ theme }) => theme.typography.lineHeight.headingFour};
  padding-block: ${({ theme }) => theme.spacing[0.25]};
  transition:
    color ${TRANSITION_DURATION} linear,
    background-color ${TRANSITION_DURATION} linear;

  span {
    color: ${({ theme }) => theme.colors.grey600};
    font-family: var(--font-inter);
    font-size: ${({ theme }) => theme.typography.fontSize.bodyXs};

    /** custom per design */
    font-weight: ${({ theme }) => theme.typography.fontWeight.body};
    line-height: ${({ theme }) => theme.typography.lineHeight.body};
    transition: color ${TRANSITION_DURATION} linear;
  }

  &:hover,
  &:focus {
    color: ${(props) => props.$accentColor};

    span {
      color: ${({ theme }) => theme.colors.grey700};
    }
  }
`;

const MobileMenu = ({ menu, onMenuClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleToggleMenu = (event) => {
    event.preventDefault();
    setIsMenuOpen((prev) => !prev);
  };
  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    onMenuClick();
  };
  const handleLinkClick = (event, trackingLabel, trackingProperty) => {
    trackLink(trackingLabel, trackingProperty);
    handleCloseMenu();
  };

  return (
    <li>
      {menu.tertiaryNav ? (
        <>
          <MenuLink
            $accentColor={menu.accentColor}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            className={cs(menu.id, isMenuOpen && 'isActive')}
            href={menu.href}
            onClick={handleToggleMenu}
            role="button"
          >
            {menu.label}
            <MenuIcon
              aria-hidden="true"
              role="img"
              size="sm"
              type="caret-down"
            />
          </MenuLink>
          <MenuContainer className={isMenuOpen ? 'isOpen' : null}>
            <MenuNav className={menu.id}>
              {menu.cta ? (
                <MenuCta
                  $accentColor={menu.accentColor}
                  cta={{
                    href: menu.cta.buttonUrl,
                    text: menu.cta.buttonText,
                    trackingLabel: menu.cta.trackingLabel,
                    trackingProperty: menu.cta.trackingProperty,
                  }}
                  onClick={handleCloseMenu}
                />
              ) : null}
              {menu.tertiaryNav.map((pillar) => (
                <PillarGroup key={pillar.id} className={pillar.id}>
                  <PillarCategory $accentColor={menu.accentColor}>
                    {pillar.icon ? <Icon size="md" type={pillar.icon} /> : null}
                    {pillar.label}
                  </PillarCategory>
                  {pillar.children
                    ? pillar.children.map((item) => (
                        <PillarLink
                          key={item.name}
                          $accentColor={menu.accentColor}
                          href={item.href}
                          rel={item.openInNewTab ? 'noopener' : undefined}
                          target={item.openInNewTab ? '_blank' : undefined}
                          onClick={(event) =>
                            handleLinkClick(
                              event,
                              menu.trackingLabel,
                              menu.trackingProperty,
                            )
                          }
                        >
                          {item.name}
                          <span>{item.description}</span>
                        </PillarLink>
                      ))
                    : null}
                </PillarGroup>
              ))}
            </MenuNav>
          </MenuContainer>
        </>
      ) : (
        <MenuLink
          $accentColor={menu.accentColor}
          className={menu.id}
          href={menu.href}
          onClick={(event) =>
            handleLinkClick(event, menu.trackingLabel, menu.trackingProperty)
          }
        >
          {menu.label}
        </MenuLink>
      )}
    </li>
  );
};

export default MobileMenu;
